import { createSelector } from 'reselect';

import { PlusBenefitState, PlusBenefitType } from '@pegasus/plus';
import { Campaign, UpcomingCampaign } from '@shared/types/mylounge';
import { CampaignFilterTags } from '@client/components/mylounge/banner/constants';
import { RootState } from '@client/redux/types';

import { CategoryTabIds } from '@shared/constants';
import { PLUS_EARLY_ACCESS_TAB_ID } from '@client/config/constants';
import { filterCampaignsStartingToday } from '@client/components/mylounge/newDeals';
import { filterCampaignsEndingToday } from '@client/components/mylounge/leavingSoon/helpers';
import {
    NAMESPACE,
    EA_CAROUSEL_CAMPAIGNS_COUNT,
    EA_UPCOMING_CAROUSEL_CAMPAIGNS_COUNT,
} from './constants';

const pickExclusive = (campaign: Campaign | UpcomingCampaign) =>
    campaign?.plusBenefit && campaign?.plusBenefit?.type === PlusBenefitType.Exclusive;

export const pickEarlyAccess = (campaign: Campaign | UpcomingCampaign) =>
    campaign?.plusBenefit &&
    campaign?.plusBenefit?.type === PlusBenefitType.EarlyAccess &&
    campaign?.plusBenefit?.state === PlusBenefitState.Active;

const filterEarlyAccess = (campaign: Campaign | UpcomingCampaign) => !pickEarlyAccess(campaign);

const filterExclusive = (campaign: Campaign | UpcomingCampaign) => !pickExclusive(campaign);

const filterTabCampaigns = (categoryTabId: CategoryTabIds) => (campaign: Campaign) =>
    campaign.tabIds.includes(categoryTabId);

const filterCampaignsByFreshness =
    (filter = CampaignFilterTags.ALL) =>
    (campaign: Campaign) => {
        if (filter === CampaignFilterTags.ALL) {
            return true;
        }

        if (
            filter === CampaignFilterTags.STARTING_TODAY &&
            new Date(campaign.startsAt).getDate() === new Date().getDate()
        ) {
            return true;
        }

        if (
            filter === CampaignFilterTags.ENDING_TODAY &&
            new Date(campaign.endsAt).getDate() === new Date().getDate()
        ) {
            return true;
        }

        return false;
    };

export const getMylounge = (state: RootState) => state[NAMESPACE];

export const getCategoryTabId = (state: RootState) => getMylounge(state).categoryTabId;

export const getCampaignFilterTag = (state: RootState) => getMylounge(state).filterTag;

export const openCampaignsSelector = (state: RootState) => getMylounge(state).openCampaigns;

const upcomingCampaignsSelector = (state: RootState) => getMylounge(state).upcomingCampaigns;

export const getGlobalRetailEvents = (state: RootState) => getMylounge(state).globalRetailEvents;

export const getTabs = (state: RootState) => state[NAMESPACE].tabs;

export const continueShoppingSelector = (state: RootState) => state.continueShopping.data;

export const getGRETab = createSelector(getTabs, tabs =>
    tabs.find(tab => tab?.globalRetailEvent || false),
);

export const getGRETabCampaigns = createSelector(
    openCampaignsSelector,
    getGRETab,
    (items = [], tab) => {
        if (!items.length || !tab?.id) return [];

        return items.filter(filterTabCampaigns(tab.id as CategoryTabIds)) ?? [];
    },
);

export const getOpenCampaigns = createSelector(
    openCampaignsSelector,
    getCategoryTabId,
    getCampaignFilterTag,
    (items = [], categoryTabId, filterTag) => {
        /**
         * TODO: Should we consider not hiding EA campaigns when the Plus feature is not enabled?
         */
        if (categoryTabId === CategoryTabIds.ALL) {
            return items.filter(
                campaign =>
                    filterTabCampaigns(categoryTabId)(campaign) &&
                    filterEarlyAccess(campaign) &&
                    filterExclusive(campaign) &&
                    filterCampaignsByFreshness(filterTag)(campaign),
            );
        }

        return items.filter(filterTabCampaigns(categoryTabId));
    },
);

export const getRegularCampaigns = (items: Campaign[]) =>
    items.filter(campaign => filterEarlyAccess(campaign) && filterExclusive(campaign));

export const getCampaignsForNewDealsToday = createSelector(openCampaignsSelector, (items = []) => {
    const regularCampaigns = getRegularCampaigns(items);

    return filterCampaignsStartingToday(regularCampaigns);
});

export const getCampaignsForLeavingSoon = createSelector(openCampaignsSelector, (items = []) => {
    const regularCampaigns = getRegularCampaigns(items);

    return filterCampaignsEndingToday(regularCampaigns);
});

export const hasOpenCampaigns = createSelector(
    openCampaignsSelector,
    (items = []) => items && items.length !== 0,
);

export const getEAOpenCampaigns = createSelector(openCampaignsSelector, (openCampaigns = []) =>
    openCampaigns
        .filter(pickEarlyAccess)
        /**
         * We only show the first 3 early access campaigns.
         */
        .slice(0, 3),
);

export const getEAOpenCampaignsForCarousel = createSelector(
    openCampaignsSelector,
    (openCampaigns = []) =>
        openCampaigns
            .filter(pickEarlyAccess)
            /**
             * We show the first 5 early access campaigns for the carousel.
             * This count can be adjusted via EA_CAROUSEL_CAMPAIGNS_COUNT constant.
             */
            .slice(0, EA_CAROUSEL_CAMPAIGNS_COUNT),
);

export const getEAUpcomingCampaigns = createSelector(
    upcomingCampaignsSelector,
    (upcomingCampaigns = []) => upcomingCampaigns.filter(pickEarlyAccess),
);

/**
 * We use this selector to get the early access campaigns for the plus signup page.
 */
export const getEarlyAccessCampaigns = createSelector(
    getEAOpenCampaigns,
    getEAUpcomingCampaigns,
    (earlyAccessOpenCampaigns = [], earlyAccessUpcomingCampaigns = []) => {
        return {
            openCampaigns: earlyAccessOpenCampaigns,
            upcomingCampaigns: earlyAccessUpcomingCampaigns,
        };
    },
);

export const getShowstopperEarlyAccessCampaigns = createSelector(
    getEarlyAccessCampaigns,
    campaigns => {
        const { openCampaigns, upcomingCampaigns } = campaigns;

        return {
            openCampaigns: openCampaigns
                .filter(campaign => campaign.tabIds.includes(CategoryTabIds.ALL))
                .slice(0, 1),
            upcomingCampaigns: upcomingCampaigns.slice(0, 1),
        };
    },
);

export const getPlusExclusiveCampaign = createSelector(openCampaignsSelector, (campaigns = []) =>
    campaigns.find(pickExclusive),
);

export const makeGetOpenCampaignById = () =>
    createSelector(
        openCampaignsSelector,
        (_: RootState, campaignId: string) => campaignId,
        (openCampaigns = [], campaignId) => {
            return openCampaigns.find(campaign => campaign.campaignId === campaignId);
        },
    );

export const makeGetUpcomingCampaigns = () =>
    createSelector(
        upcomingCampaignsSelector,
        getEAUpcomingCampaigns,
        (
            _: RootState,
            options?: {
                isPlusEnabled?: boolean;
                isPlusExclusiveEnabled?: boolean;
                isEAUpcomingCarouselEnabled?: boolean;
            },
        ) => options,
        (items = [], eaCampaigns = [], options) => {
            const { isPlusEnabled, isPlusExclusiveEnabled, isEAUpcomingCarouselEnabled } /**/ =
                options || {};

            let valueToReturn = items;

            if (isPlusExclusiveEnabled === false) {
                valueToReturn = valueToReturn.filter(filterExclusive);
            }

            if (
                isPlusEnabled === false ||
                (isEAUpcomingCarouselEnabled &&
                    eaCampaigns.length >= EA_UPCOMING_CAROUSEL_CAMPAIGNS_COUNT)
            ) {
                valueToReturn = valueToReturn.filter(filterEarlyAccess);
            }

            return valueToReturn;
        },
    );

export const makeGetForYouData = () =>
    createSelector(
        openCampaignsSelector,
        continueShoppingSelector,
        upcomingCampaignsSelector,
        getEAUpcomingCampaigns,
        (
            _: RootState,
            options?: {
                isPlusEnabled?: boolean;
                isPlusExclusiveEnabled?: boolean;
                isEAUpcomingCarouselEnabled?: boolean;
            },
        ) => options,
        (openCampaigns, continueShoppingData, upcomingCampaigns, eaCampaigns, options) => {
            const { isPlusEnabled, isPlusExclusiveEnabled, isEAUpcomingCarouselEnabled } =
                options || {};

            let filteredUpcomingCampaigns = upcomingCampaigns || [];

            if (isPlusExclusiveEnabled === false) {
                filteredUpcomingCampaigns = filteredUpcomingCampaigns.filter(filterExclusive);
            }

            if (
                isPlusEnabled === false ||
                (isEAUpcomingCarouselEnabled &&
                    eaCampaigns.length >= EA_UPCOMING_CAROUSEL_CAMPAIGNS_COUNT)
            ) {
                filteredUpcomingCampaigns = filteredUpcomingCampaigns.filter(filterEarlyAccess);
            }

            return {
                allCampaigns: openCampaigns || [],
                newDealsToday: {
                    campaigns: filterCampaignsStartingToday(openCampaigns || []),
                },
                leavingSoon: {
                    campaigns: filterCampaignsEndingToday(openCampaigns || []),
                },
                continueShopping: {
                    campaigns: continueShoppingData || [],
                },
                upcomingCampaigns: {
                    campaigns: filteredUpcomingCampaigns,
                },
            };
        },
    );

export const hasPlusEarlyAccessTab = createSelector(getTabs, tabs => {
    return tabs.findIndex(tab => tab.id === PLUS_EARLY_ACCESS_TAB_ID) !== -1;
});

export const getShowstoppers = (state: RootState) => state[NAMESPACE].showstoppers;

export const getIsLoading = (state: RootState) => state[NAMESPACE].loading;

export const getError = (state: RootState) => state[NAMESPACE].error;

export const getImagesLoaded = (state: RootState) => state[NAMESPACE].imagesLoaded;
